/**
 * SubscribersTab — who is on a plan, what it earns, and what it is giving them.
 *
 * Everything here comes from one call to /api/admin/subscriptions. Stripe can
 * already tell you what billed; it cannot tell you which customer that is,
 * which nights their block is swept, how many parks the plan has covered, or
 * how much we credited back when the city suspended a sweep. Those are the
 * columns that make this worth opening instead of the Stripe dashboard.
 *
 * Weekly and monthly plans are shown side by side, so every money figure on
 * this screen is stated per month — the API converts weekly plans at 52/12
 * before it adds anything up.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TextField,
    CircularProgress,
    IconButton,
    Tooltip,
    useMediaQuery,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CleaningCell, cleaningChipText, cleaningChipTone, nyTodayKey, dayKeyLabel, moveTimeLabel } from './cleaningDay';
import { styled } from '@mui/system';
import { getSubscriptions } from '../../services/adminService';
import { VALET, FONTS, TYPE } from '../../theme';
import { MOBILE } from './responsive';
import KpiCard from './KpiCard';

/* -------------------------------------------------------------------------- */
/* Formatting                                                                  */
/* -------------------------------------------------------------------------- */

// Whole dollars everywhere. Subscription prices are round numbers and the
// cents column is noise on a screen you scan rather than reconcile.
function money(cents) {
    if (cents === null || cents === undefined) return '—';
    return `$${Math.round(cents / 100).toLocaleString('en-US')}`;
}

function moneyExact(cents) {
    if (cents === null || cents === undefined) return '—';
    return `$${(cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

function formatDay(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysUntil(value) {
    if (!value) return null;
    const d = new Date(value);
    if (isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

function monthLabel(key) {
    const [y, m] = (key || '').split('-');
    if (!y || !m) return key;
    return new Date(Number(y), Number(m) - 1, 1)
        .toLocaleDateString('en-US', { month: 'short' });
}

/* -------------------------------------------------------------------------- */
/* Status                                                                      */
/* -------------------------------------------------------------------------- */

// One label per state, written the way it would be said out loud rather than
// the way Stripe names it. `past_due` in particular means a card is failing
// and someone should ring them, which "past due" does not say.
const STATES = {
    trial: { label: 'Free trial', bg: VALET.bronzeMuted, fg: VALET.bronze },
    active: { label: 'Active', bg: VALET.successBg, fg: VALET.success },
    cancelling: { label: 'Cancelling', bg: VALET.warningBg, fg: VALET.warning },
    past_due: { label: 'Card failing', bg: VALET.dangerBg, fg: VALET.danger },
    cancelled: { label: 'Cancelled', bg: VALET.surfaceHover, fg: VALET.textMuted },
    incomplete: { label: 'Never finished', bg: VALET.surfaceHover, fg: VALET.textMuted },
    legacy: { label: 'Old record', bg: VALET.surfaceHover, fg: VALET.textDim },
};

// The row's state as a person would describe it, which is finer than the
// stored status: an active plan can be on a free trial or already told to stop
// at the end of the period, and both change what you would do about it.
function stateOf(row) {
    if (row.legacy) return 'legacy';
    if (row.status === 'active') {
        if (row.inTrial) return 'trial';
        if (row.cancelAtPeriodEnd) return 'cancelling';
        return 'active';
    }
    return row.status || 'incomplete';
}

// Left to right in the order you would work through them: who is paying, who
// is about to start paying, who needs a phone call, then the dead ones.
const FILTERS = [
    { key: 'current', label: 'On a plan', match: (r) => r.status === 'active' || r.status === 'past_due' },
    { key: 'trial', label: 'Free trial', match: (r) => stateOf(r) === 'trial' },
    { key: 'cancelling', label: 'Cancelling', match: (r) => stateOf(r) === 'cancelling' },
    { key: 'past_due', label: 'Card failing', match: (r) => r.status === 'past_due' },
    { key: 'cancelled', label: 'Cancelled', match: (r) => r.status === 'cancelled' },
    { key: 'incomplete', label: 'Never finished', match: (r) => r.status === 'incomplete' },
    { key: 'legacy', label: 'Old records', match: (r) => r.legacy },
    { key: 'all', label: 'Everyone', match: () => true },
];

/* -------------------------------------------------------------------------- */
/* Styled                                                                      */
/* -------------------------------------------------------------------------- */

const SectionTitle = styled(Typography)({
    ...TYPE.sectionTitle,
    marginBottom: 0,
});

const Note = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    lineHeight: 1.55,
    color: VALET.textSecondary,
    marginBottom: '20px',
    maxWidth: 720,
});

const KpiGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    marginBottom: '22px',
    '@media (max-width: 1100px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    [MOBILE]: { gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' },
});

const Panel = styled(Box)({
    backgroundColor: VALET.surface,
    border: `1px solid ${VALET.border}`,
    borderRadius: '14px',
    padding: '20px 22px',
    boxShadow: VALET.shadow,
    [MOBILE]: { padding: '16px 14px' },
});

const PanelGrid = styled(Box)({
    display: 'grid',
    gridTemplateColumns: '1.4fr 1fr',
    gap: '16px',
    marginBottom: '24px',
    '@media (max-width: 900px)': { gridTemplateColumns: '1fr' },
});

const PanelHeading = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    fontWeight: 600,
    color: VALET.textPrimary,
    marginBottom: '14px',
});

const TierRow = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 0',
    borderBottom: `1px solid ${VALET.border}`,
    '&:last-of-type': { borderBottom: 'none' },
});

const TierName = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    fontWeight: 500,
    color: VALET.textPrimary,
});

const TierMeta = styled(Typography)({
    fontFamily: FONTS.mono,
    fontSize: '11.5px',
    color: VALET.textMuted,
});

const TierAmount = styled(Typography)({
    fontFamily: FONTS.mono,
    fontSize: '13px',
    fontWeight: 600,
    color: VALET.textPrimary,
    whiteSpace: 'nowrap',
});

const GrowthStrip = styled(Box)({
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
    height: 120,
});

const GrowthCol = styled(Box)({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
});

const TableWrapper = styled(Box)({
    backgroundColor: VALET.surface,
    border: `1px solid ${VALET.border}`,
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: VALET.shadow,
});

const StyledHeaderCell = styled(TableCell)({
    fontFamily: FONTS.mono,
    fontSize: '10px',
    color: VALET.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.16em',
    fontWeight: 500,
    padding: '14px 16px',
    borderBottom: `1px solid ${VALET.border}`,
    backgroundColor: VALET.surfaceMuted,
});

const StyledCell = styled(TableCell)({
    padding: '13px 16px',
    fontSize: '13px',
    fontFamily: FONTS.sans,
    color: VALET.textPrimary,
    borderBottom: `1px solid ${VALET.border}`,
    verticalAlign: 'top',
});

const SubLine = styled(Box)({
    fontFamily: FONTS.mono,
    fontSize: '11px',
    color: VALET.textMuted,
    marginTop: '3px',
});

const FilterChip = styled(Box)(({ selected }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '3px 10px',
    borderRadius: '10px',
    fontSize: '11px',
    cursor: 'pointer',
    border: '0.5px solid',
    borderColor: selected ? '#1a1a1a' : '#e0ddd6',
    backgroundColor: selected ? '#1a1a1a' : '#ffffff',
    color: selected ? '#ffffff' : '#6b6b68',
    fontFamily: FONTS.mono,
    userSelect: 'none',
    whiteSpace: 'nowrap',
}));

const ChipCount = styled('span')(({ selected }) => ({
    fontSize: '10px',
    color: selected ? 'rgba(255,255,255,0.6)' : VALET.textDim,
}));

const FilterLabel = styled(Typography)({
    fontFamily: FONTS.mono,
    fontSize: '10px',
    textTransform: 'uppercase',
    letterSpacing: '0.14em',
    color: VALET.textMuted,
    marginRight: '2px',
});

const SubCard = styled(Box)({
    padding: '14px',
    borderBottom: `1px solid ${VALET.border}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    '&:last-of-type': { borderBottom: 'none' },
});

const CardPhone = styled('a')({
    fontFamily: FONTS.mono,
    fontSize: '13px',
    color: VALET.green,
    textDecoration: 'none',
    width: 'fit-content',
});

const EmptyNote = styled(Typography)({
    fontFamily: FONTS.sans,
    fontSize: '13px',
    color: VALET.textMuted,
    textAlign: 'center',
    padding: '32px 16px',
});

const ErrorBox = styled(Typography)({
    color: VALET.danger,
    backgroundColor: VALET.dangerBg,
    fontSize: '13px',
    padding: '14px 18px',
    borderRadius: '8px',
    border: `1px solid ${VALET.danger}33`,
    fontWeight: 500,
    fontFamily: FONTS.sans,
});

function StateTag({ row }) {
    const meta = STATES[stateOf(row)] || STATES.incomplete;
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-block',
                padding: '2px 9px',
                borderRadius: '10px',
                fontSize: '11px',
                fontFamily: FONTS.mono,
                whiteSpace: 'nowrap',
                backgroundColor: meta.bg,
                color: meta.fg,
            }}
        >
            {meta.label}
        </Box>
    );
}

/* -------------------------------------------------------------------------- */
/* Detail                                                                      */
/* -------------------------------------------------------------------------- */

// The rest of the row, opened by clicking it. Kept out of the table proper
// because none of it is worth a column of its own — it is what you want after
// a name has already caught your eye.
function Detail({ row, todayKey }) {
    const cleaning = row.cleaning || { hasSchedule: false };
    const next = cleaning.next;
    const nextText = !cleaning.hasSchedule
        ? 'No schedule set'
        : !cleaning.active
            ? 'Paused — nothing is being booked'
            : next
                ? `${dayKeyLabel(next.dateKey, todayKey)}, ${moveTimeLabel(next)}`
                : 'Nothing in the next few weeks';
    const leadHours = cleaning.reminderLeadMin ? Math.round(cleaning.reminderLeadMin / 60) : 0;

    const items = [
        ['Address on file', row.address || '—'],
        ['Cleaning day', cleaning.label || 'None set'],
        ['Next move', nextText],
        // Only worth a line when the two copies disagree: the plan was sold on
        // one schedule, the customer has since changed theirs, and the van
        // follows the customer.
        ...(cleaning.matchesPlan === false
            ? [['Plan was sold as', `${cleaning.planLabel} — the customer's own schedule wins`]]
            : []),
        ['Schedule set by', cleaning.hasSchedule
            ? (cleaning.from === 'customer' ? 'The customer, in the app' : 'The plan they bought')
            : '—'],
        ['Reminded', cleaning.reminderLeadMin
            ? (cleaning.reminderLeadMin >= 60
                ? `${leadHours} hour${leadHours === 1 ? '' : 's'} before`
                : `${cleaning.reminderLeadMin} minutes before`)
            : '—'],
        ['Promo used', row.promoCode || '—'],
        ['Free trial ends', row.trialEndsAt ? formatDay(row.trialEndsAt) : '—'],
        ['Started', formatDay(row.startedAt)],
        ['This period ends', formatDay(row.currentPeriodEnd)],
        ['Cancelled', row.cancelledAt ? formatDay(row.cancelledAt) : '—'],
        ['Invoices paid', `${row.paymentsCount} · ${moneyExact(row.paidCents)}`],
        ['Parks covered', `${row.coveredOrders} · worth ${moneyExact(row.coveredValueCents)}`],
        ['Suspension credits', row.creditedDays
            ? `${row.creditedDays} day${row.creditedDays === 1 ? '' : 's'} · ${moneyExact(row.creditedCents)}`
            : 'None'],
        ['Stripe subscription', row.stripeSubscriptionId || '—'],
        ['Stripe customer', row.stripeCustomerId || '—'],
    ];
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: '12px 24px',
                padding: '4px 2px 8px',
            }}
        >
            {items.map(([label, value]) => (
                <Box key={label}>
                    <Box sx={{
                        fontFamily: FONTS.mono,
                        fontSize: '10px',
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: VALET.textMuted,
                        mb: '3px',
                    }}>
                        {label}
                    </Box>
                    <Box sx={{
                        fontFamily: FONTS.sans,
                        fontSize: '12.5px',
                        color: VALET.textPrimary,
                        wordBreak: 'break-all',
                    }}>
                        {value}
                    </Box>
                </Box>
            ))}
        </Box>
    );
}

/* -------------------------------------------------------------------------- */

export default function SubscribersTab() {
    const isMobile = useMediaQuery(MOBILE.replace('@media ', ''));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('current');
    const [tierFilter, setTierFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [newestFirst, setNewestFirst] = useState(true);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await getSubscriptions();
            setData(res && res.success ? res.data : res);
        } catch (err) {
            setError(err.message || 'Failed to load subscribers');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const rows = useMemo(() => (data && Array.isArray(data.rows) ? data.rows : []), [data]);
    const summary = (data && data.summary) || null;
    // New York's date, from the server that books the moves. Falls back to the
    // browser's own reading of New York if an older payload has no stamp.
    const todayKey = (data && data.todayKey) || nyTodayKey();

    const filterCounts = useMemo(() => {
        const counts = {};
        FILTERS.forEach((f) => { counts[f.key] = rows.filter(f.match).length; });
        return counts;
    }, [rows]);

    // Rows the Show filter admits, before the plan filter. Both the plan chip
    // counts and the visible list are cut from this, so a chip never claims
    // more than clicking it would produce.
    const inScope = useMemo(() => {
        const def = FILTERS.find((f) => f.key === filter) || FILTERS[FILTERS.length - 1];
        return rows.filter(def.match);
    }, [rows, filter]);

    const tierChips = useMemo(() => {
        if (!summary) return [];
        return summary.byTier
            .map((t) => ({ ...t, count: inScope.filter((r) => r.tier === t.tier).length }))
            .filter((t) => t.count > 0);
    }, [summary, inScope]);

    const visible = useMemo(() => {
        const q = search.trim().toLowerCase();
        const out = inScope.filter((r) => {
            if (tierFilter !== 'all' && r.tier !== tierFilter) return false;
            if (!q) return true;
            return (
                (r.name || '').toLowerCase().includes(q) ||
                (r.phone || '').toLowerCase().includes(q) ||
                (r.address || '').toLowerCase().includes(q) ||
                ((r.cleaning && r.cleaning.shortLabel) || '').toLowerCase().includes(q) ||
                (r.promoCode || '').toLowerCase().includes(q)
            );
        });
        return out.sort((a, b) => {
            const da = new Date(a.startedAt).getTime() || 0;
            const db = new Date(b.startedAt).getTime() || 0;
            return newestFirst ? db - da : da - db;
        });
    }, [inScope, tierFilter, search, newestFirst]);

    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
                <CircularProgress sx={{ color: VALET.green }} />
            </Box>
        );
    }

    if (error) return <ErrorBox>{error}</ErrorBox>;
    if (!summary) return <ErrorBox>No subscriber data came back.</ErrorBox>;

    const needsAttention = summary.pastDue + summary.cancelAtPeriodEnd;
    const growthMax = Math.max(
        1,
        ...summary.growth.map((g) => Math.max(g.started, g.cancelled))
    );

    // One sentence that says what the four numbers above add up to, and the
    // one thing worth doing about them today.
    const headline = summary.active === 0
        ? 'Nobody is on a plan yet. The moment somebody buys one, they show up here.'
        : `${summary.active} ${summary.active === 1 ? 'person is' : 'people are'} on a plan, `
          + `bringing in ${money(summary.mrrCents)} every month`
          + (summary.trialing ? ` — plus ${summary.trialing} still on a free trial, worth ${money(summary.trialMrrCents)} a month once they start paying.` : '.')
          + (needsAttention
              ? ` ${needsAttention} ${needsAttention === 1 ? 'needs' : 'need'} a look: `
                + [
                    summary.pastDue ? `${summary.pastDue} card${summary.pastDue === 1 ? '' : 's'} failing` : null,
                    summary.cancelAtPeriodEnd ? `${summary.cancelAtPeriodEnd} cancelling` : null,
                  ].filter(Boolean).join(' and ')
                + '. Tap "Card failing" or "Cancelling" below and call them.'
              : '');

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                mb: '10px',
            }}>
                <SectionTitle>Subscribers</SectionTitle>
                <Tooltip title="Reload">
                    <span>
                        <IconButton onClick={load} disabled={loading} size="small" sx={{ color: VALET.textMuted }}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Note>{headline}</Note>

            <KpiGrid>
                <KpiCard
                    label="Money every month"
                    value={money(summary.mrrCents)}
                    subtitle={`${money(summary.arrCents)} a year if nobody leaves`}
                />
                <KpiCard
                    label="Paying now"
                    value={summary.active - summary.trialing}
                    subtitle={
                        summary.newLast30
                            ? `${summary.newLast30} started in the last 30 days`
                            : 'None started in the last 30 days'
                    }
                    subtitleColor={summary.newLast30 ? 'pos' : 'neu'}
                />
                <KpiCard
                    label="On a free trial"
                    value={summary.trialing}
                    subtitle={
                        summary.trialing
                            ? `${money(summary.trialMrrCents)} a month once they convert`
                            : 'Nobody is trialling right now'
                    }
                />
                <KpiCard
                    label="Needs a call"
                    value={needsAttention}
                    subtitle={
                        needsAttention
                            ? `${summary.pastDue} card failing · ${summary.cancelAtPeriodEnd} cancelling`
                            : 'Everything is healthy'
                    }
                    subtitleColor={needsAttention ? 'neg' : 'pos'}
                />
            </KpiGrid>

            <PanelGrid>
                <Panel>
                    <PanelHeading>What they are buying</PanelHeading>
                    {summary.byTier.map((t) => (
                        <TierRow key={t.tier}>
                            <Box>
                                <TierName>{t.name}</TierName>
                                <TierMeta>
                                    {t.active} {t.active === 1 ? 'subscriber' : 'subscribers'}
                                </TierMeta>
                            </Box>
                            <TierAmount>{money(t.mrrCents)}/mo</TierAmount>
                        </TierRow>
                    ))}
                    <TierRow>
                        <Box>
                            <TierName>How they pay</TierName>
                            <TierMeta>
                                {summary.byInterval.week} weekly · {summary.byInterval.month} monthly
                            </TierMeta>
                        </Box>
                        <TierAmount>{money(summary.mrrCents)}/mo</TierAmount>
                    </TierRow>
                </Panel>

                <Panel>
                    <PanelHeading>Started and cancelled, last 6 months</PanelHeading>
                    <GrowthStrip>
                        {summary.growth.map((g) => (
                            <GrowthCol key={g.month}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    gap: '3px',
                                    height: 80,
                                }}>
                                    <Tooltip title={`${g.started} started`} placement="top" arrow>
                                        <Box sx={{
                                            width: 12,
                                            height: `${Math.max(2, (g.started / growthMax) * 80)}px`,
                                            backgroundColor: VALET.green,
                                            borderRadius: '3px 3px 0 0',
                                        }} />
                                    </Tooltip>
                                    <Tooltip title={`${g.cancelled} cancelled`} placement="top" arrow>
                                        <Box sx={{
                                            width: 12,
                                            height: `${Math.max(2, (g.cancelled / growthMax) * 80)}px`,
                                            backgroundColor: VALET.danger,
                                            opacity: 0.55,
                                            borderRadius: '3px 3px 0 0',
                                        }} />
                                    </Tooltip>
                                </Box>
                                <Box sx={{
                                    fontFamily: FONTS.mono,
                                    fontSize: '10px',
                                    color: VALET.textMuted,
                                }}>
                                    {monthLabel(g.month)}
                                </Box>
                            </GrowthCol>
                        ))}
                    </GrowthStrip>
                    <Box sx={{
                        fontFamily: FONTS.sans,
                        fontSize: '11.5px',
                        color: VALET.textMuted,
                        mt: '10px',
                    }}>
                        Dark green is people who started. Red is people who left. You want the
                        green bar taller than the red one every month.
                    </Box>
                </Panel>
            </PanelGrid>

            <Panel sx={{ mb: '24px' }}>
                <PanelHeading>What the plans have given back</PanelHeading>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                    gap: '16px',
                }}>
                    {[
                        ['Parks covered', summary.coveredOrders, 'jobs subscribers did not pay per-park for'],
                        ['That would have cost', money(summary.coveredValueCents), 'at the normal per-park price'],
                        ['Collected all time', money(summary.lifetimeRevenueCents), 'every subscription invoice ever paid'],
                        ['Credited back', money(summary.creditedCents), 'for days the city suspended cleaning'],
                    ].map(([label, value, hint]) => (
                        <Box key={label}>
                            <Box sx={{ ...TYPE.kpiLabel, mb: '6px' }}>{label}</Box>
                            <Box sx={{
                                fontFamily: FONTS.display,
                                fontSize: '22px',
                                fontWeight: 500,
                                letterSpacing: '-0.02em',
                                color: VALET.ink,
                            }}>
                                {value}
                            </Box>
                            <Box sx={{
                                fontFamily: FONTS.sans,
                                fontSize: '11.5px',
                                color: VALET.textMuted,
                                mt: '4px',
                            }}>
                                {hint}
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Panel>

            {/* Controls */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: '16px' }}>
                <TextField
                    size="small"
                    placeholder="Search name, phone, address, cleaning day, or promo code"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        width: 380,
                        maxWidth: '100%',
                        '& .MuiOutlinedInput-root': {
                            fontSize: '13px',
                            borderRadius: '10px',
                            fontFamily: FONTS.sans,
                            backgroundColor: VALET.bg,
                        },
                    }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <FilterLabel>Show</FilterLabel>
                    {FILTERS.filter(
                        (f) => f.key === 'current' || f.key === 'all' || filterCounts[f.key] > 0
                    ).map((f) => (
                        <FilterChip
                            key={f.key}
                            selected={filter === f.key}
                            onClick={() => { setFilter(f.key); setOpenId(null); }}
                        >
                            {f.label}
                            <ChipCount selected={filter === f.key}>{filterCounts[f.key]}</ChipCount>
                        </FilterChip>
                    ))}
                </Box>

                {tierChips.length > 1 && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        <FilterLabel>Plan</FilterLabel>
                        <FilterChip selected={tierFilter === 'all'} onClick={() => setTierFilter('all')}>
                            All plans
                        </FilterChip>
                        {tierChips.map((t) => (
                            <FilterChip
                                key={t.tier}
                                selected={tierFilter === t.tier}
                                onClick={() => setTierFilter(t.tier)}
                            >
                                {t.name}
                                <ChipCount selected={tierFilter === t.tier}>{t.count}</ChipCount>
                            </FilterChip>
                        ))}
                    </Box>
                )}

                <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                    <FilterChip selected={false} onClick={() => setNewestFirst((v) => !v)}>
                        {newestFirst ? 'Newest first' : 'Oldest first'}
                    </FilterChip>
                </Box>
            </Box>

            <TableWrapper>
                {isMobile ? (
                    <Box>
                        {visible.map((r) => {
                            const open = openId === r._id;
                            const left = daysUntil(r.currentPeriodEnd);
                            return (
                                <SubCard key={r._id} onClick={() => setOpenId(open ? null : r._id)}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        justifyContent: 'space-between',
                                        gap: '10px',
                                    }}>
                                        <Box sx={{
                                            fontFamily: FONTS.sans,
                                            fontSize: '15px',
                                            fontWeight: 600,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {r.name}
                                        </Box>
                                        <Box sx={{
                                            fontFamily: FONTS.mono,
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                        }}>
                                            {money(r.amountCents)}/{r.interval === 'week' ? 'wk' : 'mo'}
                                        </Box>
                                    </Box>
                                    {r.phone && (
                                        <CardPhone
                                            href={`tel:${r.phone.replace(/[^\d+]/g, '')}`}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {r.phone}
                                        </CardPhone>
                                    )}
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        flexWrap: 'wrap',
                                        fontFamily: FONTS.mono,
                                        fontSize: '11.5px',
                                        color: VALET.textMuted,
                                    }}>
                                        <StateTag row={r} />
                                        <span>{r.tierName}</span>
                                        {left !== null && left >= 0 && (
                                            <span style={{ color: VALET.textDim }}>
                                                {r.cancelAtPeriodEnd ? 'ends' : 'renews'} in {left}d
                                            </span>
                                        )}
                                    </Box>
                                    {cleaningChipText(r.cleaning, todayKey) && (
                                        <Box sx={{
                                            fontFamily: FONTS.mono,
                                            fontSize: '11.5px',
                                            color: cleaningChipTone(r.cleaning, todayKey),
                                        }}>
                                            {cleaningChipText(r.cleaning, todayKey)}
                                        </Box>
                                    )}
                                    {open && <Detail row={r} todayKey={todayKey} />}
                                </SubCard>
                            );
                        })}
                        {visible.length === 0 && (
                            <EmptyNote>
                                {rows.length === 0
                                    ? 'Nobody has bought a plan yet.'
                                    : 'Nothing matches those filters.'}
                            </EmptyNote>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 1140 }}>
                            <TableHead>
                                <TableRow>
                                    <StyledHeaderCell>Customer</StyledHeaderCell>
                                    <StyledHeaderCell>Plan</StyledHeaderCell>
                                    <StyledHeaderCell>Cleaning day</StyledHeaderCell>
                                    <StyledHeaderCell>Pays</StyledHeaderCell>
                                    <StyledHeaderCell>Status</StyledHeaderCell>
                                    <StyledHeaderCell>Started</StyledHeaderCell>
                                    <StyledHeaderCell>Renews</StyledHeaderCell>
                                    <StyledHeaderCell align="right">Used</StyledHeaderCell>
                                    <StyledHeaderCell align="right">Paid so far</StyledHeaderCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visible.map((r) => {
                                    const open = openId === r._id;
                                    const left = daysUntil(r.currentPeriodEnd);
                                    return (
                                        <React.Fragment key={r._id}>
                                            <TableRow
                                                onClick={() => setOpenId(open ? null : r._id)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    '&:hover td': { backgroundColor: VALET.surfaceHover },
                                                    '& td': open ? { borderBottom: 'none' } : {},
                                                }}
                                            >
                                                <StyledCell sx={{ fontWeight: 600 }}>
                                                    {r.name}
                                                    {r.phone && <SubLine>{r.phone}</SubLine>}
                                                </StyledCell>
                                                <StyledCell>
                                                    {r.tierName}
                                                    <SubLine>
                                                        {r.movesPerWeek
                                                            ? `${r.movesPerWeek} move${r.movesPerWeek === 1 ? '' : 's'}/wk`
                                                            : r.address || '—'}
                                                    </SubLine>
                                                </StyledCell>
                                                <StyledCell>
                                                    <CleaningCell cleaning={r.cleaning} todayKey={todayKey} />
                                                </StyledCell>
                                                <StyledCell sx={{ fontFamily: FONTS.mono, whiteSpace: 'nowrap' }}>
                                                    {money(r.amountCents)}
                                                    <Box component="span" sx={{ color: VALET.textMuted }}>
                                                        /{r.interval === 'week' ? 'wk' : 'mo'}
                                                    </Box>
                                                    {r.interval === 'week' && (
                                                        <SubLine>{money(r.monthlyEquivalentCents)}/mo</SubLine>
                                                    )}
                                                </StyledCell>
                                                <StyledCell>
                                                    <StateTag row={r} />
                                                    {r.promoCode && <SubLine>{r.promoCode}</SubLine>}
                                                </StyledCell>
                                                <StyledCell sx={{
                                                    fontFamily: FONTS.mono,
                                                    fontSize: '12px',
                                                    color: VALET.textMuted,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {formatDay(r.startedAt)}
                                                </StyledCell>
                                                <StyledCell sx={{
                                                    fontFamily: FONTS.mono,
                                                    fontSize: '12px',
                                                    color: VALET.textMuted,
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {r.status === 'active' || r.status === 'past_due'
                                                        ? formatDay(r.currentPeriodEnd)
                                                        : '—'}
                                                    {(r.status === 'active' || r.status === 'past_due') && left !== null && left >= 0 && (
                                                        <SubLine>
                                                            {r.cancelAtPeriodEnd ? 'ends in' : 'in'} {left}d
                                                        </SubLine>
                                                    )}
                                                </StyledCell>
                                                <StyledCell align="right" sx={{ fontFamily: FONTS.mono, fontSize: '13px' }}>
                                                    {r.coveredOrders}
                                                    <SubLine>{money(r.coveredValueCents)} of parks</SubLine>
                                                </StyledCell>
                                                <StyledCell align="right" sx={{
                                                    fontFamily: FONTS.mono,
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                }}>
                                                    {money(r.paidCents)}
                                                    <SubLine>
                                                        {r.paymentsCount} invoice{r.paymentsCount === 1 ? '' : 's'}
                                                    </SubLine>
                                                </StyledCell>
                                            </TableRow>
                                            {open && (
                                                <TableRow>
                                                    <StyledCell colSpan={9} sx={{ backgroundColor: VALET.surfaceMuted }}>
                                                        <Detail row={r} todayKey={todayKey} />
                                                    </StyledCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                                {visible.length === 0 && (
                                    <TableRow>
                                        <StyledCell colSpan={9} sx={{
                                            textAlign: 'center',
                                            color: VALET.textMuted,
                                            padding: '32px 16px',
                                        }}>
                                            {rows.length === 0
                                                ? 'Nobody has bought a plan yet.'
                                                : 'Nothing matches those filters.'}
                                        </StyledCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </TableWrapper>
        </Box>
    );
}
